import { jsx as _jsx } from "react/jsx-runtime";
import React, { createContext, useCallback, useContext, useEffect, useReducer } from "react";
import { AUTH_CHALLENGES } from "@/constants/auth";
import useAuthStore from "@/context/auth/useAuthStore";
import { organizationStore } from "@/context/organization/useOrganizationStore";
import useServices from "@/context/services/useServices";
import { queryClient } from "@/lib/react-query/config";
import { AUTH_ACTION_TYPES } from "./actionTypes";
import { clearStoredSessionData, createAuthUser, processSessionAndDispatch } from "./authSession";
const initialState = {
    user: null,
    isAuthenticated: false,
    isLoading: true,
    error: null,
    initialized: false,
    challengeData: null,
    mfaSetupRequired: false
};
function authReducer(state, action) {
    switch(action.type){
        case AUTH_ACTION_TYPES.AUTH_START:
            return {
                ...state,
                isLoading: true,
                error: null
            };
        case AUTH_ACTION_TYPES.AUTH_SUCCESS:
            {
                const mfaSetupRequired = action.payload.mfaSetupRequired || false;
                return {
                    ...state,
                    user: action.payload.user,
                    isAuthenticated: !mfaSetupRequired,
                    isLoading: false,
                    error: null,
                    challengeData: null,
                    initialized: true,
                    mfaSetupRequired
                };
            }
        case AUTH_ACTION_TYPES.AUTH_FAILURE:
            return {
                ...state,
                user: null,
                isAuthenticated: false,
                isLoading: false,
                error: action.payload.error,
                challengeData: null
            };
        case AUTH_ACTION_TYPES.AUTH_CHALLENGE:
            return {
                ...state,
                user: null,
                isAuthenticated: false,
                isLoading: false,
                error: null,
                challengeData: action.payload.challengeData
            };
        case AUTH_ACTION_TYPES.AUTH_LOGOUT:
            return {
                ...state,
                user: null,
                isAuthenticated: false,
                isLoading: false,
                error: null,
                challengeData: null,
                initialized: true
            };
        case AUTH_ACTION_TYPES.CLEAR_ERROR:
            return {
                ...state,
                error: null
            };
        case AUTH_ACTION_TYPES.CLEAR_CHALLENGE:
            return {
                ...state,
                challengeData: null
            };
        case AUTH_ACTION_TYPES.SET_INITIALIZED:
            return {
                ...state,
                initialized: true,
                isLoading: false
            };
        case AUTH_ACTION_TYPES.CLEAR_MFA_SETUP_REQUIRED:
            return {
                ...state,
                mfaSetupRequired: false,
                isAuthenticated: state.user !== null
            };
        default:
            return state;
    }
}
const AuthContext = /*#__PURE__*/ createContext(undefined);
export const AuthProvider = ({ children })=>{
    const [state, dispatch] = useReducer(authReducer, initialState);
    const { authService, mfaService } = useServices();
    const { clearTokenData, setSessionData } = useAuthStore();
    useEffect(()=>{
        const initializeAuth = async ()=>{
            try {
                // Check if we have valid HTTP-only cookies by calling the session endpoint
                const sessionData = await authService.checkSession();
                if (!processSessionAndDispatch({
                    sessionData,
                    setSessionData,
                    dispatch
                })) {
                    // No valid session
                    dispatch({
                        type: AUTH_ACTION_TYPES.SET_INITIALIZED
                    });
                }
            } catch (error) {
                if (error && typeof error === "object" && "response" in error) {
                    const response = error.response;
                    if (response?.status === 403 && response.data?.message === "MFA setup required") {
                        const fallbackUser = createAuthUser("");
                        clearStoredSessionData(setSessionData);
                        dispatch({
                            type: AUTH_ACTION_TYPES.AUTH_SUCCESS,
                            payload: {
                                user: fallbackUser,
                                mfaSetupRequired: true
                            }
                        });
                        return;
                    }
                }
                // Session check failed (401 or network error)
                dispatch({
                    type: AUTH_ACTION_TYPES.SET_INITIALIZED
                });
            }
        };
        initializeAuth();
    }, []); // Empty dependency array - only run once on mount
    const login = async (credentials)=>{
        try {
            dispatch({
                type: AUTH_ACTION_TYPES.AUTH_START
            });
            const response = await authService.login(credentials);
            if ("challengeName" in response) {
                // Handle any challenge type (NEW_PASSWORD_REQUIRED, SOFTWARE_TOKEN_MFA, SMS_MFA, MFA_SETUP)
                dispatch({
                    type: AUTH_ACTION_TYPES.AUTH_CHALLENGE,
                    payload: {
                        challengeData: {
                            challengeName: response.challengeName,
                            session: response.session,
                            email: response.email
                        }
                    }
                });
                return;
            }
            if ("success" in response && response.success) {
                // Check if org-enforced MFA setup is required
                const mfaSetupRequired = response.mfaSetupRequired || false;
                // If MFA setup is required, avoid loading the full session to limit access
                if (mfaSetupRequired) {
                    const user = createAuthUser(response.email);
                    clearStoredSessionData(setSessionData);
                    dispatch({
                        type: AUTH_ACTION_TYPES.AUTH_SUCCESS,
                        payload: {
                            user,
                            mfaSetupRequired
                        }
                    });
                    return;
                }
                // After successful login, fetch full session data to populate auth store
                try {
                    const sessionData = await authService.checkSession();
                    processSessionAndDispatch({
                        sessionData,
                        setSessionData,
                        dispatch,
                        mfaSetupRequired,
                        fallbackEmail: response.email
                    });
                } catch  {
                    // Still consider login successful with basic info
                    const user = createAuthUser(response.email);
                    dispatch({
                        type: AUTH_ACTION_TYPES.AUTH_SUCCESS,
                        payload: {
                            user,
                            mfaSetupRequired
                        }
                    });
                }
                return;
            }
            throw new Error("Unexpected response format");
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : "Login failed";
            dispatch({
                type: AUTH_ACTION_TYPES.AUTH_FAILURE,
                payload: {
                    error: errorMessage
                }
            });
            throw error;
        }
    };
    const changePassword = async (data)=>{
        try {
            dispatch({
                type: AUTH_ACTION_TYPES.AUTH_START
            });
            const response = await authService.changePassword(data);
            // Check if MFA setup is required after password change
            if ("challengeName" in response && response.challengeName === AUTH_CHALLENGES.MFA_SETUP) {
                dispatch({
                    type: AUTH_ACTION_TYPES.AUTH_CHALLENGE,
                    payload: {
                        challengeData: {
                            challengeName: response.challengeName,
                            session: response.session,
                            email: response.email
                        }
                    }
                });
                return;
            }
            // Check if org-enforced MFA setup is required
            const mfaSetupRequired = "mfaSetupRequired" in response && response.mfaSetupRequired || false;
            if (mfaSetupRequired) {
                const user = createAuthUser(response.email);
                clearStoredSessionData(setSessionData);
                dispatch({
                    type: AUTH_ACTION_TYPES.AUTH_SUCCESS,
                    payload: {
                        user,
                        mfaSetupRequired
                    }
                });
                return;
            }
            // After successful password change, fetch full session data
            try {
                const sessionData = await authService.checkSession();
                processSessionAndDispatch({
                    sessionData,
                    setSessionData,
                    dispatch,
                    mfaSetupRequired,
                    fallbackEmail: response.email
                });
            } catch  {
                // Still consider password change successful
                const user = createAuthUser(response.email);
                dispatch({
                    type: AUTH_ACTION_TYPES.AUTH_SUCCESS,
                    payload: {
                        user,
                        mfaSetupRequired
                    }
                });
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : "Password change failed";
            dispatch({
                type: AUTH_ACTION_TYPES.AUTH_FAILURE,
                payload: {
                    error: errorMessage
                }
            });
            throw error;
        }
    };
    const submitMfaChallenge = async (data)=>{
        if (!state.challengeData || state.challengeData.challengeName !== AUTH_CHALLENGES.SOFTWARE_TOKEN_MFA && state.challengeData.challengeName !== AUTH_CHALLENGES.SMS_MFA) {
            throw new Error("No MFA challenge pending");
        }
        const challengeEmail = state.challengeData.email;
        try {
            dispatch({
                type: AUTH_ACTION_TYPES.AUTH_START
            });
            await mfaService.challenge({
                email: state.challengeData.email,
                code: data.code,
                session: state.challengeData.session,
                challengeName: state.challengeData.challengeName
            });
            // After successful MFA, fetch full session data
            try {
                const sessionData = await authService.checkSession();
                processSessionAndDispatch({
                    sessionData,
                    setSessionData,
                    dispatch,
                    fallbackEmail: challengeEmail
                });
            } catch  {
                const user = createAuthUser(challengeEmail);
                dispatch({
                    type: AUTH_ACTION_TYPES.AUTH_SUCCESS,
                    payload: {
                        user
                    }
                });
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : "MFA verification failed";
            dispatch({
                type: AUTH_ACTION_TYPES.AUTH_FAILURE,
                payload: {
                    error: errorMessage
                }
            });
            throw error;
        }
    };
    const logout = async ()=>{
        try {
            await authService.logout();
            clearTokenData(); // Clear JWT token data from store
            dispatch({
                type: AUTH_ACTION_TYPES.AUTH_LOGOUT
            });
        } catch (error) {
            // logout locally even if network fails
            clearTokenData();
            dispatch({
                type: AUTH_ACTION_TYPES.AUTH_LOGOUT
            });
        }
        // Force clear localStorage auth data to ensure clean state
        localStorage.removeItem("auth-token-storage");
        // Clear all cached queries from react-query
        queryClient.clear();
        // Clear organization store data
        organizationStore.getState().actions.reset();
    };
    const clearError = useCallback(()=>{
        dispatch({
            type: AUTH_ACTION_TYPES.CLEAR_ERROR
        });
    }, []);
    const clearMfaSetupRequired = useCallback(()=>{
        dispatch({
            type: AUTH_ACTION_TYPES.CLEAR_MFA_SETUP_REQUIRED
        });
    }, []);
    const contextValue = {
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        isLoading: state.isLoading,
        error: state.error,
        initialized: state.initialized,
        challengeData: state.challengeData,
        mfaSetupRequired: state.mfaSetupRequired,
        login,
        changePassword,
        submitMfaChallenge,
        logout,
        clearError,
        clearMfaSetupRequired
    };
    return /*#__PURE__*/ _jsx(AuthContext.Provider, {
        value: contextValue,
        children: children
    });
};
export const useAuth = ()=>{
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
};
